import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { clearAllPersistedFilters } from '../lib/persistentFilters';

// Color palettes keyed by theme_color name
const THEME_PALETTES = {
  teal:    { 50: '#f0fdfa', 100: '#ccfbf1', 200: '#99f6e4', 500: '#14b8a6', 600: '#0d9488', 700: '#0f766e', 800: '#115e59' },
  indigo:  { 50: '#eef2ff', 100: '#e0e7ff', 200: '#c7d2fe', 500: '#6366f1', 600: '#4f46e5', 700: '#4338ca', 800: '#3730a3' },
  amber:   { 50: '#fffbeb', 100: '#fef3c7', 200: '#fde68a', 500: '#f59e0b', 600: '#d97706', 700: '#b45309', 800: '#92400e' },
  rose:    { 50: '#fff1f2', 100: '#ffe4e6', 200: '#fecdd3', 500: '#f43f5e', 600: '#e11d48', 700: '#be123c', 800: '#9f1239' },
  violet:  { 50: '#f5f3ff', 100: '#ede9fe', 200: '#ddd6fe', 500: '#8b5cf6', 600: '#7c3aed', 700: '#6d28d9', 800: '#5b21b6' },
  emerald: { 50: '#ecfdf5', 100: '#d1fae5', 200: '#a7f3d0', 500: '#10b981', 600: '#059669', 700: '#047857', 800: '#065f46' },
  sky:     { 50: '#f0f9ff', 100: '#e0f2fe', 200: '#bae6fd', 500: '#0ea5e9', 600: '#0284c7', 700: '#0369a1', 800: '#075985' },
  slate:   { 50: '#f8fafc', 100: '#f1f5f9', 200: '#e2e8f0', 500: '#64748b', 600: '#475569', 700: '#334155', 800: '#1e293b' },
};

function applyThemeToRoot(themeColor) {
  const palette = THEME_PALETTES[themeColor] || THEME_PALETTES.teal;
  const root = document.documentElement;
  root.style.setProperty('--c-50', palette[50]);
  root.style.setProperty('--c-100', palette[100]);
  root.style.setProperty('--c-200', palette[200]);
  root.style.setProperty('--c-500', palette[500]);
  root.style.setProperty('--c-600', palette[600]);
  root.style.setProperty('--c-700', palette[700]);
  root.style.setProperty('--c-800', palette[800]);
}

const CompanyContext = createContext({
  activeCompany: null,
  companies: [],
  selectCompany: () => {},
  switchCompany: () => {},
  setUserCompanies: () => {},
  clearCompany: () => {},
  isCompanySelected: false,
  themeColor: 'teal',
});

export const CompanyProvider = ({ children }) => {
  const [activeCompany, setActiveCompany] = useState(null);
  const [companies, setCompanies] = useState([]);

  // Load persisted state on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('active_company');
      const storedCompanies = localStorage.getItem('user_companies');
      if (stored) {
        const parsed = JSON.parse(stored);
        setActiveCompany(parsed);
        applyThemeToRoot(parsed.theme_color || 'teal');
      }
      if (storedCompanies) setCompanies(JSON.parse(storedCompanies));
    } catch {
      localStorage.removeItem('active_company');
      localStorage.removeItem('active_company_id');
      localStorage.removeItem('user_companies');
    }
  }, []);

  const selectCompany = useCallback((company) => {
    // Clear persisted filters when switching to a DIFFERENT company
    const prevId = localStorage.getItem('active_company_id');
    if (prevId && prevId !== company.company_id) {
      clearAllPersistedFilters();
    }
    setActiveCompany(company);
    localStorage.setItem('active_company', JSON.stringify(company));
    localStorage.setItem('active_company_id', company.company_id);
    applyThemeToRoot(company.theme_color || 'teal');
  }, []);

  const setUserCompanies = useCallback((companiesList) => {
    setCompanies(companiesList);
    localStorage.setItem('user_companies', JSON.stringify(companiesList));
    
    // Auto-select if only one company
    if (companiesList.length === 1) {
      selectCompany(companiesList[0]);
    }
  }, [selectCompany]);

  const switchCompany = useCallback((companyId) => {
    const company = companies.find(c => c.company_id === companyId);
    if (company) {
      selectCompany(company);
    }
  }, [companies, selectCompany]);

  const clearCompany = useCallback(() => {
    setActiveCompany(null);
    setCompanies([]);
    localStorage.removeItem('active_company');
    localStorage.removeItem('active_company_id');
    localStorage.removeItem('user_companies');
    clearAllPersistedFilters();
    applyThemeToRoot('teal');
  }, []);

  const themeColor = activeCompany?.theme_color || 'teal';

  return (
    <CompanyContext.Provider value={{
      activeCompany,
      companies,
      selectCompany,
      switchCompany,
      setUserCompanies,
      clearCompany,
      isCompanySelected: !!activeCompany,
      themeColor,
    }}>
      {children}
    </CompanyContext.Provider>
  );
};

export const useCompany = () => useContext(CompanyContext);
export { THEME_PALETTES };
